import EmptyState from 'components/EmptyState/EmptyState';
import WidgetLoadingAnimation from 'components/Widgets/WidgetLoadingAnimation';
import WidgetTimeUpdate from 'components/Widgets/WidgetTimeUpdate';
import WeatherCardGrid from 'features/Weather/components/WeatherCardGrid/WeatherCardGrid';
import { getForecastDateLabel } from 'features/Weather/utils/forecastDate.utils';
import useLocation from 'hooks/useLocation';
import { useMemo } from 'react';
import { Container } from 'react-bootstrap';
import { GiBuoy } from 'react-icons/gi';
import { useGetBuoyConditionsQuery } from 'services/weather/marine/marine.api';
import MarineAccordion from '../../components/MarineAccordion/MarineAccordion';
import { buoyConditionDetails, route } from './config/buoyConditions.config';
import {
  getBuoyConditionLocations,
  getBuoyLocationSubtitle,
  getBuoyObservationCardKey,
  getBuoyObservationCardTitle,
} from './utils/buoyConditions.utils';

const BuoyConditions = () => {
  const { locations } = useLocation();
  const effectiveLocation = locations.effectiveLocation;
  const {
    data: buoyConditions = [],
    isLoading: isLoadingBuoyConditions,
    isFetching: isFetchingBuoyConditions,
    fulfilledTimeStamp,
  } = useGetBuoyConditionsQuery(effectiveLocation);
  const isRefreshing = isFetchingBuoyConditions && !isLoadingBuoyConditions;

  const buoyLocations = useMemo(() => getBuoyConditionLocations(buoyConditions), [buoyConditions]);

  return (
    <Container
      fluid
      className="buoy-conditions weather-data-display d-flex h-100 flex-column gap-3 px-0 text-white user-select-none"
    >
      <WidgetTimeUpdate route={route} updatedAt={fulfilledTimeStamp ?? null} />
      {isLoadingBuoyConditions ? (
        <div className="d-flex h-100 align-items-center justify-content-center mt-3">
          <WidgetLoadingAnimation />
        </div>
      ) : !buoyLocations.length ? (
        <EmptyState
          title="Buoy Conditions"
          message="No buoy condition data available for this location."
        />
      ) : (
        <>
          <div className="weather-data-display-heading d-flex align-items-start justify-content-between gap-3 border-bottom pb-2">
            <h2 className="weather-data-display-title m-0 fw-bold lh-sm">
              {getForecastDateLabel(new Date())}
            </h2>
            {isRefreshing && (
              <span className="buoy-conditions-page-status small" role="status">
                Updating...
              </span>
            )}
          </div>
          <div className="buoy-conditions-scroll flex-grow-1 overflow-auto">
            <div className="d-flex flex-column gap-3 pb-4">
              {buoyLocations.map((location) => (
                <MarineAccordion
                  key={location.locationKey}
                  eventKey={location.locationKey}
                  title={location.locationName}
                  subtitle={getBuoyLocationSubtitle(location)}
                  headingLevel="h3"
                >
                  <WeatherCardGrid
                    entries={location.observations}
                    details={buoyConditionDetails}
                    getCardKey={getBuoyObservationCardKey}
                    getCardTitle={getBuoyObservationCardTitle}
                    getCardIcon={() => <GiBuoy size={56} />}
                    containerClassName="buoy-conditions-card-grid"
                    detailsContainerClassName="buoy-conditions-card-details gap-2"
                    cardHeadingLevel="h4"
                    emptyMessage={`No observations available for ${location.locationName}.`}
                  />
                </MarineAccordion>
              ))}
            </div>
          </div>
        </>
      )}
    </Container>
  );
};

export default BuoyConditions;
